import {
  Badge,
  Box,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router";
import Button from "./Button";
import { useTranslation } from "react-i18next";
import DropDownMenu, { DropDownMenuItem } from "./DropDownMenu";
import { FC, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Icons from "../assets/Icons";

interface RightButtonProps {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  isBadgeEnable?: boolean;
  badgeCount?: number;
  disabled?: boolean;
  iconPosition?: "left" | "right";
  isDropDown?: boolean;
  dropDownMenuItems?: Array<DropDownMenuItem>;
  sx?: SxProps<Theme>;
}

// Shared look for page-header action buttons (Export, Filters, Edit…).
export const headerActionButtonSx = {
  borderRadius: "8px",
  border: "none",
  backgroundColor: "#5921F2",
  color: "#FFFFFF",
  fontWeight: 700,
  textDecoration: "none",
  height: 56,
  fontSize: 16,
  textTransform: "none",
  "&:hover": { backgroundColor: "#4A1BC7", border: "none" },
  // Icon SVGs hardcode a grey fill; follow the button's white text instead.
  "& .MuiButton-startIcon svg [fill]:not([fill='none']), & .MuiButton-endIcon svg [fill]:not([fill='none'])":
    { fill: "currentColor" },
} as const;

type ScreenTopBarProps = {
  title: string;
  back?: boolean;
  disableButton?: boolean;
  onSaveClick?: () => void;
  showSaveButton?: boolean;
  btnText?: string;
  rightButtonsArray?: RightButtonProps[];
  saveButtonSx?: SxProps<Theme>;
};

const ScreenTopBar = ({
  title,
  back,
  disableButton,
  onSaveClick,
  showSaveButton = true,
  btnText,
  rightButtonsArray,
  saveButtonSx,
}: ScreenTopBarProps) => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        padding: "24px 16px",
        height: "50px",
        backgroundColor: "custom.background.primary",
        borderBottom: "1px solid #E5E7EB",
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        {back && (
          <IconButton size="small" onClick={() => navigate(-1)}>
            <Icons.BackButton />
          </IconButton>
        )}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "32px",
            letterSpacing: "-0.48px",
            color: "#101828",
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        {rightButtonsArray?.map((button) => {
          return <RightButton key={button.label} {...button} />;
        })}
        {showSaveButton && (
          <Button
            onClick={onSaveClick}
            disabled={disableButton}
            variant="contained"
            sx={
              saveButtonSx ?? {
                backgroundColor: disableButton
                  ? "custom.foreground.inactive"
                  : "custom.background.brand",
                color: `${palette.custom?.foreground?.inverted} !important`,
                mx: 1.5,
                my: 0.5,
              }
            }
          >
            {btnText ?? t("Save_Settings")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const RightButton: FC<RightButtonProps> = ({
  label,
  icon,
  iconPosition = "left",
  onClick,
  isBadgeEnable,
  badgeCount,
  disabled,
  isDropDown = false,
  dropDownMenuItems = [],
  sx,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDropDownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const wrappedMenuItems = dropDownMenuItems.map((item) => ({
    ...item,
    onClick: () => {
      item.onClick();
      handleMenuClose();
    },
  }));

  const buttonIcon = (() => {
    if (isDropDown) {
      if (anchorEl) {
        return <ExpandLessIcon sx={{ width: 26, height: 26 }} />;
      } else {
        return <ExpandMoreIcon sx={{ width: 26, height: 26 }} />;
      }
    }
    return icon;
  })();

  return (
    <>
      <Button
        key={label}
        onClick={isDropDown ? handleDropDownClick : onClick}
        variant="outlined"
        disabled={disabled}
        startIcon={iconPosition === "left" ? buttonIcon : undefined}
        endIcon={iconPosition === "right" ? buttonIcon : undefined}
        sx={
          sx ?? {
            ...headerActionButtonSx,
            pl: 2,
            pr: isBadgeEnable ? 3.5 : 2,
          }
        }
      >
        {t(label)}
        {isBadgeEnable && (
          <Badge
            badgeContent={badgeCount}
            sx={{
              marginLeft: 2,
              "& .MuiBadge-badge": {
                backgroundColor: "#FFFFFF",
                color: "#5921F2",
              },
            }}
          />
        )}
      </Button>
      {isDropDown && (
        <DropDownMenu
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          menuItems={wrappedMenuItems}
        />
      )}
    </>
  );
};

export default ScreenTopBar;
